"use client";

import React, {
  MutableRefObject,
  SyntheticEvent,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  forwardRef
} from "react";
import { PortableText, PortableTextComponents } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { PortableTextBlock } from "sanity";
import Image from "next/image";
import { cn } from "@/lib/utils";
import { GROQImage } from "@/sanity/lib/types";

import arch from "@/public/home/arch.svg";

// Custom rich text component for testimonials that inherits text color
const testimonialRichTextComponent: PortableTextComponents = {
  block: {
    normal: ({ children }) => <span>{children}</span>,
  },
};

interface ITestimonial {
  country: string;
  flagIcon: GROQImage;
  quote: PortableTextBlock[];
  name: string;
  rating: number;
}

export interface TestimonialSliderRef {
  goToSlide: (index: number) => void;
  activeIndex: number;
}

class EventEmitter {
  private events: { [key: string]: Function[] } = {};

  addEventListener(event: string, callback: Function) {
    if (!this.events[event]) {
      this.events[event] = [];
    }
    this.events[event].push(callback);
  }

  dispatchEvent(event: string, data?: any) {
    if (this.events[event]) {
      this.events[event].forEach((callback) => callback({ data }));
    }
  }
}

class TestimonialSliderInstance extends EventEmitter {
  animating: boolean = false;
  lastRaf: number = 0;

  container: HTMLElement | null = null;
  slides: HTMLElement[] = [];
  slidesX: number[] = [];

  gap: number = 0; // in rem
  itemWidth: number = 0; // in rem

  freeScroll: boolean = false;
  mouseStartX: number = 0;
  mouseStartPos: number = 0;
  mouseX: number = 0;
  wheelSpeed: number = 0.6; // 1.5x speed (0.4 * 1.5)
  dragSpeed: number = 1.4; // 1.4x drag speed multiplier
  wheelTimeout: number = 0;

  slideX: number = 0;
  targetX: number = 0;
  activeIndex: number = 0; // Visual index (including clones)
  realActiveIndex: number = 0; // Real index (0 to numberOfSlides-1)
  numberOfSlides: number = 0; // Number of real slides
  clonesPerSide: number = 2; // Number of clones on each side
  isRepositioning: boolean = false; // Flag to disable lerp during instant repositioning

  maxRotation: number = 9; // degrees (2D rotation)
  disableCurvature: boolean = false; // Flag to disable curvature effect

  // Cache for rem size in pixels
  private remSizeCache: number = 16;
  private resizeTimeout: number = 0;

  constructor(public numSlides: number, gap: number = 0, itemWidth: number = 0, disableCurvature: boolean = false) {
    super();
    this.numberOfSlides = numSlides;
    this.gap = gap; // stored in rem
    this.itemWidth = itemWidth; // stored in rem
    this.disableCurvature = disableCurvature;
    this.maxRotation = disableCurvature ? 0 : 9; // Disable rotation if flat carousel
    this.updateRemSize();
    // Start at first real slide (after clones)
    this.activeIndex = this.clonesPerSide;
    this.realActiveIndex = 0;
  }

  // Get actual computed rem size from browser
  updateRemSize() {
    if (typeof window !== 'undefined') {
      const rootFontSize = parseFloat(
        getComputedStyle(document.documentElement).fontSize
      );
      this.remSizeCache = rootFontSize || 16;
    }
  }

  // Convert rem to pixels using cached value
  remToPx(rem: number): number {
    return rem * this.remSizeCache;
  }

  getItemWidth() {
    // Responsive width based on screen size
    if (typeof window !== 'undefined' && window.innerWidth < 768) {
      // Mobile: use container width minus padding
      return (this.container?.offsetWidth ?? 0) / this.remSizeCache - 4; // Subtract 4rem for padding
    }
    return this.itemWidth; // 76.875rem (desktop)
  }

  getGap() {
    // Responsive gap based on screen size
    if (typeof window !== 'undefined' && window.innerWidth < 768) {
      return 2; // Smaller gap on mobile
    }
    return this.gap; // 5.87rem (desktop)
  }

  init(container: HTMLElement) {
    if (!container) {
      console.error("No container found!");
      return;
    }
    // @ts-ignore
    window["testimonialSlider"] = this;
    this.container = container;
    this.slides = Array.from(container.children as any);
    this.slides.forEach((slide, index) => {
      slide.setAttribute("data-index", index.toString());
    });
    this.slidesX = new Array(this.slides.length).fill(0);
    this.animating = true;
    this.raf();

    // Add resize listener with 10ms debounce
    this.handleResize = this.handleResize.bind(this);
    window.addEventListener('resize', this.handleResize);
  }

  handleResize = () => {
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = window.setTimeout(() => {
      this.updateRemSize();
    }, 10);
  };

  mathSnapNumber(start: number, max: number, target: number) {
    if (Math.abs(start) < max) {
      return target;
    }
    return start;
  }

  lerp(start: number, end: number, t: number) {
    return this.mathSnapNumber(start * (1 - t) + end * t, 0.01, 0);
  }

  render() {
    if (this.slides.length > 0) {
      const gapPx = this.remToPx(this.getGap());
      const widthPx = this.remToPx(this.getItemWidth());
      const containerWidth = this.container?.offsetWidth ?? 0;
      const centerX = containerWidth / 2;

      if (!this.freeScroll) {
        this.targetX = -this.getSlideX(this.activeIndex) + centerX - widthPx / 2;
      }
      // Don't reposition during active dragging - only on snap/settle to avoid glitches

      // Use instant positioning during repositioning, otherwise lerp
      if (this.isRepositioning) {
        this.slideX = this.targetX;
        this.isRepositioning = false;
      } else {
        this.slideX = this.lerp(this.slideX, this.targetX, 0.15);
      }

      let x = this.slideX;
      this.slides.forEach((slide, index) => {
        this.slidesX[index] = x;
        const roundedX = Math.round(x * 100) / 100;

        // Calculate rotation based on distance from center
        const slideCenter = x + widthPx / 2;
        const distanceFromCenter = slideCenter - centerX;
        const maxDistance = widthPx + gapPx;

        // Calculate normalized distance (unclamped for progressive rotation)
        const normalizedDistance = distanceFromCenter / maxDistance;

        // Progressive rotation with soft limit (using tanh for smooth saturation)
        const rotationMultiplier = Math.tanh(normalizedDistance * 1.2); // Smooth saturation
        const rotation = rotationMultiplier * this.maxRotation * 1.6; // Max ~14.4° for adjacent cards

        // Determine if this is the active slide (based on realActiveIndex)
        const realIndex = this.getRealIndex(index);
        const isActive = realIndex === this.realActiveIndex;

        // Visual effects based on absolute distance
        const absDistance = Math.abs(normalizedDistance);
        const scale = isActive ? 1 : Math.max(0.75, 1 - Math.min(absDistance, 2) * 0.05);
        const opacity = Math.max(0.3, 1 - Math.min(absDistance, 2) * 0.3);
        const zIndex = 100 - Math.min(Math.abs(Math.round(normalizedDistance * 50)), 99);

        // Calculate Y offset - skip completely if curvature is disabled
        let yOffsetFromRotation = 0;

        if (!this.disableCurvature) {
          const rotationRadians = (rotation * Math.PI) / 180;
          const cardHeight = this.remToPx(43.53713);

          // Corner alignment formula for proper bottom corner alignment
          const cornerAlignmentY =
            (cardHeight / 2) * (1 - Math.cos(rotationRadians)) +
            (widthPx / 2) * Math.abs(Math.sin(rotationRadians));

          // For 2nd+ order cards, add extra drop based on distance
          // Use a softer curve so 1st cards stay aligned, but 2nd+ cards drop more
          let extraDrop = 0;
          if (absDistance > 1.5) {
            // Only apply extra drop to cards beyond 1.5 distance
            const excessDistance = absDistance - 1.5;
            extraDrop = excessDistance * excessDistance * this.remToPx(8);
          }

          yOffsetFromRotation = cornerAlignmentY + extraDrop;
        }

        const roundedY = Math.round(yOffsetFromRotation * 100) / 100;

        slide.style.transform = `translate3d(${roundedX}px, ${roundedY}px, 0) rotate(${rotation}deg) scale(${scale})`;
        slide.style.opacity = opacity.toString();
        slide.style.zIndex = zIndex.toString();

        // Set data attribute for active state
        slide.setAttribute('data-active', isActive ? 'true' : 'false');

        x += widthPx + gapPx;
      });
    }

    if (this.animating) {
      this.raf();
    }
  }

  // Convert visual index to real index (handle clones)
  getRealIndex(visualIndex: number): number {
    if (visualIndex < this.clonesPerSide) {
      // Left clones: map to end of real slides
      return this.numberOfSlides - (this.clonesPerSide - visualIndex);
    } else if (visualIndex >= this.clonesPerSide + this.numberOfSlides) {
      // Right clones: map to start of real slides
      return visualIndex - this.clonesPerSide - this.numberOfSlides;
    } else {
      // Real slides
      return visualIndex - this.clonesPerSide;
    }
  }

  stop() {
    this.animating = false;
    cancelAnimationFrame(this.lastRaf);
    this.lastRaf = 0;
    window.removeEventListener('resize', this.handleResize);
    clearTimeout(this.resizeTimeout);
    clearTimeout(this.repositionCheckTimeout);
  }

  checkBoundariesDuringScroll(centerX: number, widthPx: number, gapPx: number) {
    // Find which slide is currently centered
    let closestIndex = 0;
    let minDistance = Infinity;

    this.slidesX.forEach((x, index) => {
      const slideCenter = x + widthPx / 2;
      const distance = Math.abs(slideCenter - centerX);
      if (distance < minDistance) {
        minDistance = distance;
        closestIndex = index;
      }
    });

    // If we're on a clone during free scroll, reposition
    if (closestIndex < this.clonesPerSide) {
      // On left clones - jump to right
      const offset = closestIndex;
      const targetIndex = this.clonesPerSide + this.numberOfSlides + offset;
      const shift = this.getSlideX(targetIndex) - this.getSlideX(closestIndex);
      this.targetX -= shift;
      this.slideX -= shift;
      this.activeIndex = targetIndex;
      this.realActiveIndex = this.getRealIndex(targetIndex);
    } else if (closestIndex >= this.clonesPerSide + this.numberOfSlides) {
      // On right clones - jump to left
      const offset = closestIndex - (this.clonesPerSide + this.numberOfSlides);
      const targetIndex = this.clonesPerSide + offset;
      const shift = this.getSlideX(targetIndex) - this.getSlideX(closestIndex);
      this.targetX -= shift;
      this.slideX -= shift;
      this.activeIndex = targetIndex;
      this.realActiveIndex = this.getRealIndex(targetIndex);
    }
  }

  raf() {
    this.lastRaf = requestAnimationFrame(() => this.render());
  }

  onMouseDown(e: SyntheticEvent<HTMLDivElement, MouseEvent>) {
    e.preventDefault();
    this.freeScroll = true;
    this.mouseStartPos = this.slideX;
    this.mouseStartX = e.nativeEvent.clientX;
    this.mouseX = e.nativeEvent.clientX;
  }

  onTouchStart(e: SyntheticEvent<HTMLDivElement, TouchEvent>) {
    if (e.nativeEvent.touches.length < 1) {
      return;
    }
    this.freeScroll = true;
    this.mouseStartPos = this.slideX;
    this.mouseStartX = e.nativeEvent.touches[0].clientX;
    this.mouseX = e.nativeEvent.touches[0].clientX;
  }

  onMouseMove(e: SyntheticEvent<HTMLDivElement, MouseEvent>) {
    if (this.freeScroll) {
      e.preventDefault();
      this.mouseX = e.nativeEvent.clientX;
      const delta = this.mouseX - this.mouseStartX;
      this.targetX = this.mouseStartPos + (delta * this.dragSpeed);
    }
  }

  onTouchMove(e: SyntheticEvent<HTMLDivElement, TouchEvent>) {
    if (this.freeScroll) {
      if (e.nativeEvent.touches.length < 1) {
        return;
      }
      this.mouseX = e.nativeEvent.touches[0].clientX;
      const delta = this.mouseX - this.mouseStartX;
      this.targetX = this.mouseStartPos + (delta * this.dragSpeed);
    }
  }

  onScroll(e: SyntheticEvent<HTMLDivElement, WheelEvent>) {
    if (Math.abs(e.nativeEvent.deltaX) === 0) {
      return;
    }

    this.freeScroll = true;
    this.mouseStartPos = this.slideX;
    this.targetX += -e.nativeEvent.deltaX * this.wheelSpeed;

    window.clearTimeout(this.wheelTimeout);
    this.wheelTimeout = window.setTimeout(() => {
      this.freeScroll = false;
      this.snapToClosest();
    }, 100);
  }

  onMouseUp(e: SyntheticEvent<HTMLDivElement, MouseEvent>) {
    e.preventDefault();
    this.freeScroll = false;
    this.snapToClosest();

    if (this.mouseStartX === e.nativeEvent.clientX) {
      const target = e.target as HTMLElement;
      const slideElement = target.closest('div[aria-roledescription="slide"]');
      if (slideElement) {
        const index = parseInt(slideElement.getAttribute("data-index") ?? "0");
        if (index !== this.activeIndex) {
          this.slideTo(index);
        }
      }
    }
  }

  onTouchEnd(e: SyntheticEvent<HTMLDivElement, TouchEvent>) {
    const delta = this.mouseX - this.mouseStartX;
    this.targetX = this.mouseStartPos + (delta * this.dragSpeed * 1.5); // 1.5x momentum for touch
    this.freeScroll = false;
    this.snapToClosest();
  }

  getClosestIndex() {
    const containerWidth = this.container?.offsetWidth ?? 0;
    const centerX = containerWidth / 2;
    const widthPx = this.remToPx(this.getItemWidth());

    let closest = 0;
    let minDistance = Infinity;

    this.slidesX.forEach((x, index) => {
      const slideCenter = x + widthPx / 2;
      const distance = Math.abs(slideCenter - centerX);
      if (distance < minDistance) {
        minDistance = distance;
        closest = index;
      }
    });

    return closest;
  }

  snapToClosest() {
    const index = this.getClosestIndex();
    this.slideTo(index);

    // Check if we need to reposition (if we're on a clone)
    this.checkAndRepositionIfNeeded();
  }

  checkAndRepositionIfNeeded() {
    const totalSlides = this.numberOfSlides + (this.clonesPerSide * 2);

    // If on left clones, jump to corresponding real slide on the right
    if (this.activeIndex < this.clonesPerSide) {
      const targetIndex = this.numberOfSlides + this.activeIndex;
      this.isRepositioning = true;
      this.activeIndex = targetIndex;
      this.targetX = -this.getSlideX(this.activeIndex) + (this.container?.offsetWidth ?? 0) / 2 - this.remToPx(this.getItemWidth()) / 2;
    }
    // If on right clones, jump to corresponding real slide on the left
    else if (this.activeIndex >= this.clonesPerSide + this.numberOfSlides) {
      const targetIndex = this.clonesPerSide + (this.activeIndex - this.clonesPerSide - this.numberOfSlides);
      this.isRepositioning = true;
      this.activeIndex = targetIndex;
      this.targetX = -this.getSlideX(this.activeIndex) + (this.container?.offsetWidth ?? 0) / 2 - this.remToPx(this.getItemWidth()) / 2;
    }

    // Update real active index
    this.realActiveIndex = this.getRealIndex(this.activeIndex);
  }

  getSlideX(index: number) {
    const gapPx = this.remToPx(this.getGap());
    const widthPx = this.remToPx(this.getItemWidth());
    return index * (widthPx + gapPx);
  }

  slideTo(index: number) {
    const totalSlides = this.numberOfSlides + (this.clonesPerSide * 2);
    if (index >= 0 && index < totalSlides) {
      this.activeIndex = index;
      this.realActiveIndex = this.getRealIndex(index);
      // Dispatch the real index, not the visual index
      this.dispatchEvent("change", this.realActiveIndex);

      // Check if we need to reposition after settling
      this.scheduleRepositionCheck();
    }
  }

  private repositionCheckTimeout: number = 0;

  scheduleRepositionCheck() {
    clearTimeout(this.repositionCheckTimeout);
    // Check after animation has time to settle
    this.repositionCheckTimeout = window.setTimeout(() => {
      this.checkAndRepositionIfNeeded();
    }, 500); // Wait for animation to mostly complete
  }

  // Navigate to a real slide index (wraps infinitely)
  slideToReal(realIndex: number) {
    // Wrap the real index
    const wrappedIndex = ((realIndex % this.numberOfSlides) + this.numberOfSlides) % this.numberOfSlides;
    // Convert to visual index (add offset for left clones)
    const visualIndex = wrappedIndex + this.clonesPerSide;
    this.slideTo(visualIndex);
  }
}

function TestimonialCard({ testimonial, isActive, futures = false }: { testimonial: ITestimonial; isActive: boolean; futures?: boolean }) {
  return (
    <div
      className={cn(
        "w-full h-full rounded-[1.75rem] flex flex-col transition-all duration-500 ease-out",
        isActive ? (futures ? "bg-[#060626] text-white" : "bg-[#103037] text-white") : "bg-white text-[#103037]"
      )}
      style={{
        padding: "6rem 6rem 5rem"
      }}
    >
      <div className="flex-1 mb-[2rem] relative">
        <svg className="relative w-[4.7rem] h-[3.85763rem] mb-[5.6rem]" width="38" height="31" viewBox="0 0 38 31" fill="none">
          <path d="M9.58793 14.9811H15.88V30.8612H0V16.6291L7.19095 0H13.9325L9.58793 14.9811ZM31.4604 14.9811H37.7525V30.8612H21.8725V16.6291L29.0634 0H35.8049L31.4604 14.9811Z" fill={futures ? "#FFEEBE" : "#99C2B1"}/>
        </svg>
        <div
          className={cn(
            "richtext richtext-start prose-p:font-medium font-medium prose-p:text-[3rem] text-[3rem] prose-p:tracking-tight tracking-tight prose-p:leading-[1.6] leading-[1.6]",
            isActive ? "prose-p:text-white text-white" : "prose-p:text-[#103037] text-[#103037]"
          )}
        >
          <PortableText
            value={testimonial.quote}
            components={testimonialRichTextComponent}
          />
        </div>
      </div>

      <div className="flex items-center justify-between pt-[2rem]">
        <div>
          <p className={cn(
            "text-[2.5rem] mb-0 transition-colors duration-500",
            isActive ? "text-white/70" : "text-[#103037]/70"
          )}>{testimonial.name}</p>
        </div>
        <div className="flex gap-[0.5rem]">
          {Array.from({ length: 5 }).map((_, index) => (
            <svg
              key={index}
              className={cn(
                "w-[2.44075rem] h-[2.32138rem]",
                index < testimonial.rating
                  ? "fill-[#FFD700]"
                  : isActive ? "fill-white/20" : "fill-[#103037]/20"
              )}
              width="20" height="19" viewBox="0 0 20 19" fill="none"
            >
              <path d="M9.76283 0L6.74511 6.11504L0 7.08999L4.88142 11.852L3.72739 18.5706L9.76283 15.4003L15.7983 18.5706L14.6443 11.852L19.5257 7.09663L12.7806 6.11504L9.76283 0Z" fill="#161616"/>
              <path d="M9.76283 0L6.74511 6.11504L0 7.08999L4.88142 11.852L3.72739 18.5706L9.76283 15.4003L15.7983 18.5706L14.6443 11.852L19.5257 7.09663L12.7806 6.11504L9.76283 0Z" fill="#DEC695"/>
            </svg>
          ))}
        </div>
      </div>
    </div>
  );
}

function TestimonialNavButton({
  disabled,
  onClick,
  reversed,
}: {
  disabled: boolean;
  onClick: () => void;
  reversed?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "w-[4rem] h-[4rem] rounded-full bg-white border border-[#E5E5E5] flex items-center justify-center transition-opacity",
        disabled && "opacity-40 pointer-events-none"
      )}
    >
      <svg
        className={cn("w-[2rem] h-[2rem]", reversed && "rotate-180")}
        viewBox="0 0 32.402 32.402"
        fill="none"
      >
        <path
          d="M9,6l8.1,8.1L9,22.2"
          transform="translate(3.151 2.101)"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
        />
      </svg>
    </button>
  );
}

function TestimonialNavBar({
  numSlides,
  activeIndex,
  onClick,
}: {
  numSlides: number;
  activeIndex: number;
  onClick: (index: number) => void;
}) {
  return (
    <div className="flex flex-row justify-center items-center gap-[1rem]">
      {Array.from({ length: numSlides }).map((_, index) => (
        <div
          key={index}
          onClick={() => onClick(index)}
          className={cn(
            "h-[0.5rem] transition-all duration-300",
            activeIndex === index
              ? "w-[5rem] bg-[#489268]"
              : "w-[2rem] bg-[#E5E5E5] cursor-pointer"
          )}
        />
      ))}
    </div>
  );
}

const TestimonialSlider = forwardRef<TestimonialSliderRef, {
  testimonials: ITestimonial[];
  onActiveIndexChange?: (index: number) => void;
  disableCurvature?: boolean;
  futures?: boolean;
}>(({ testimonials, onActiveIndexChange, disableCurvature = false, futures = false }, ref) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const sliderRef = useRef<HTMLDivElement>(null);
  const sliderInstance: MutableRefObject<TestimonialSliderInstance | undefined> =
    useRef();
  const activeIndexTimeoutRef = useRef<number>(0);

  const numSlides = useMemo(() => testimonials.length, [testimonials.length]);

  // Create cloned testimonials array
  const clonesPerSide = 2;
  const clonedTestimonials = useMemo(() => {
    if (testimonials.length === 0) return [];

    const leftClones = testimonials.slice(-clonesPerSide); // Last N items
    const rightClones = testimonials.slice(0, clonesPerSide); // First N items

    return [...leftClones, ...testimonials, ...rightClones];
  }, [testimonials]);

  // Expose methods to parent
  useImperativeHandle(ref, () => ({
    goToSlide: (index: number) => {
      sliderInstance.current?.slideToReal(index);
    },
    activeIndex,
  }));

  useEffect(() => {
    sliderInstance.current = new TestimonialSliderInstance(
      testimonials.length,
      5.87, // gap: 5.87rem (fixed)
      76.875, // width: 76.875rem (fixed)
      disableCurvature
    );
    if (sliderInstance.current && sliderRef.current) {
      sliderInstance.current.init(sliderRef.current);

      sliderInstance.current?.addEventListener("change", (index: any) => {
        // Debounce active index updates to prevent flickering during repositioning
        clearTimeout(activeIndexTimeoutRef.current);
        activeIndexTimeoutRef.current = window.setTimeout(() => {
          setActiveIndex(index.data);
          onActiveIndexChange?.(index.data);
        }, 50); // 50ms debounce to smooth out rapid changes
      });
    }

    return () => {
      sliderInstance.current?.stop();
      clearTimeout(activeIndexTimeoutRef.current);
    };
  }, [testimonials.length, onActiveIndexChange]);

  return (
    <div className="w-full relative pb-[10rem] lg:pb-[15rem] lg:[mask-image:linear-gradient(to_right,transparent_0%,black_15%,black_85%,transparent_100%)] lg:[-webkit-mask-image:linear-gradient(to_right,transparent_0%,black_15%,black_85%,transparent_100%)]">
      <Image
        className="!w-full absolute hidden lg:block top-[13rem] left-0"
        src={arch}
        alt=""
      />

      <div
        className="w-[calc(100%-4rem)] relative h-[50rem] md:h-[43.5371rem] flex items-center justify-center"
        ref={sliderRef}
        aria-roledescription="carousel"
        aria-label="Testimonials carousel"
        role="region"
        onMouseDown={(e) => sliderInstance.current?.onMouseDown(e)}
        onTouchStart={(e) => sliderInstance.current?.onTouchStart(e)}
        onMouseMove={(e) => sliderInstance.current?.onMouseMove(e)}
        onTouchMove={(e) => sliderInstance.current?.onTouchMove(e)}
        onMouseUp={(e) => sliderInstance.current?.onMouseUp(e)}
        onTouchEnd={(e) => sliderInstance.current?.onTouchEnd(e)}
        onMouseLeave={(e) => sliderInstance.current?.onMouseUp(e)}
        onWheel={(e) => sliderInstance.current?.onScroll(e)}
      >
        {clonedTestimonials.map((testimonial, visualIndex) => {
          // Calculate real index for this testimonial
          const realIndex = visualIndex < clonesPerSide
            ? testimonials.length - (clonesPerSide - visualIndex)
            : visualIndex >= clonesPerSide + testimonials.length
              ? visualIndex - clonesPerSide - testimonials.length
              : visualIndex - clonesPerSide;

          return (
            <div
              key={visualIndex}
              data-real-index={realIndex}
              className="absolute w-[min(76.875rem,100%)] md:h-[43.53713rem] top-0 left-0"
              aria-roledescription="slide"
              role="group"
            >
              <TestimonialCard testimonial={testimonial} isActive={realIndex === activeIndex} futures={futures} />
            </div>
          );
        })}
      </div>

      <div className="w-full !hidden  flex-row justify-between items-center mt-[5rem]">
        <TestimonialNavButton
          disabled={false}
          onClick={() => sliderInstance.current?.slideToReal(activeIndex - 1)}
          reversed={true}
        />
        <TestimonialNavBar
          numSlides={numSlides}
          activeIndex={activeIndex}
          onClick={(index) => sliderInstance.current?.slideToReal(index)}
        />
        <TestimonialNavButton
          disabled={false}
          onClick={() => sliderInstance.current?.slideToReal(activeIndex + 1)}
        />
      </div>
    </div>
  );
});

TestimonialSlider.displayName = 'TestimonialSlider';

export default TestimonialSlider;
export type { ITestimonial };
